import { useCallback, useContext, useRef, useState } from "react"
import { useTranslation } from "react-i18next"
import type { Client } from "@xmpp/client"
import { useAppDispatch } from "@/reducers/hooks"
import { setAllWorkflows } from "@/reducers/openclawWorkflowsSlice"
import { retrieveAllWorkflows } from "@/api/xeps/OpenclawWorkflows"
import { isNotFoundError } from "@/api/xeps/adhocForm"
import { XmppContext } from "@/stream/xmppClient"

// Translation keys: the hook shows them in the UI language when the error
// happens.
export const ALREADY_RAN = "ai.automationMutation.alreadyRan" as const

export const CRON_GONE = "ai.automationMutation.gone" as const

type NotFoundMessage = typeof ALREADY_RAN | typeof CRON_GONE

/**
 * Shared write path for every automation surface — the route stop's dialog and
 * both automations-list dialogs go through this, so none of them can grow a
 * different answer to "the rule changed under me while the form was open".
 */
export function useAutomationMutation(
  notFoundMessage: NotFoundMessage = ALREADY_RAN,
) {
  const { t } = useTranslation()
  // The connected client comes from the provider every caller already sits
  // under — reaching for `window.XMPP` would tie this hook to app bootstrap.
  const { client } = useContext(XmppContext)
  const dispatch = useAppDispatch()
  const [saving, setSaving] = useState(false)
  // The button's disabled state lags a render behind, so two quick clicks can
  // both reach this hook. The ref is what actually keeps one write in flight.
  const inFlight = useRef(false)
  const [error, setError] = useState<string | null>(null)

  /**
   * The node is live, so rules normally appear and disappear on their own.
   * After a not-found answer we re-read it explicitly: the local copy is known
   * to be stale at that exact moment.
   */
  const refresh = useCallback(async () => {
    if (!client) return
    const fresh = await retrieveAllWorkflows(client)
    dispatch(setAllWorkflows({ workflows: fresh }))
  }, [client, dispatch])

  /** Resolves true when the agent accepted the command. */
  const run = useCallback(
    async (
      command: (client: Client) => Promise<unknown>,
      onNotFound?: () => void,
    ): Promise<boolean> => {
      if (inFlight.current) return false
      // The client can exist mid-reconnect with `.jid` still unset until the
      // new connection finishes binding — agentJid() throws on that, so treat
      // it the same as "no client".
      if (!client || !client.jid) {
        setError(t("ai.automationMutation.unreachable"))
        return false
      }

      inFlight.current = true
      setSaving(true)
      setError(null)
      try {
        await command(client)
        return true
      } catch (err) {
        if (isNotFoundError(err)) {
          // Never retry or re-create a per-stop rule: the stop's arrival is
          // already recorded, so a resurrected rule could neither fire nor
          // clean itself up.
          setError(t(notFoundMessage))
          onNotFound?.()
          await refresh()
        } else {
          setError(
            err instanceof Error && err.message
              ? t("ai.automationMutation.saveFailed", { message: err.message })
              : t("ai.automationMutation.unreachable"),
          )
        }
        return false
      } finally {
        inFlight.current = false
        setSaving(false)
      }
    },
    [client, notFoundMessage, refresh, t],
  )

  return { saving, error, setError, run }
}
